// LocationHelp — tells a rep their location is off and how to turn it back on
// (Oct 27, 2026). Two shapes: a strip under the canvas top bar, and a card on a
// canvasser's home so the setting gets fixed before they reach a door.

import { useState } from "react";
import {
  locate, locationSteps, phoneKind, problemText, useGeoPermission,
  type GeoProblem, type GeoResult,
} from "../../lib/geo";

function Steps({ onRetry }: { onRetry: () => Promise<GeoResult> }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const steps = locationSteps(phoneKind());
  async function retry() {
    setBusy(true);
    setMsg(null);
    const r = await onRetry();
    setBusy(false);
    setMsg(r.ok ? "Location is working." : problemText(r.problem));
  }
  return (
    <div className="mt-2">
      <ol className="list-decimal space-y-1 pl-5 text-xs">
        {steps.map((s) => <li key={s}>{s}</li>)}
      </ol>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void retry()}
          disabled={busy}
          className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60"
        >
          {busy ? "Checking…" : "Try again"}
        </button>
        {msg && <span className="text-xs">{msg}</span>}
      </div>
    </div>
  );
}

/** Strip under the canvas top bar while the phone isn't giving a location. */
export function LocationBanner({ problem, onRetry }: { problem: GeoProblem; onRetry: () => Promise<GeoResult> }) {
  const [open, setOpen] = useState(problem === "denied");
  const tone = problem === "denied" || problem === "unsupported"
    ? "border-red-300 bg-red-50 text-red-900"
    : "border-amber-300 bg-amber-50 text-amber-900";
  return (
    <div className={`z-20 border-b px-3 py-2 text-sm ${tone}`} role="alert">
      <div className="flex items-start gap-2">
        <span className="text-base">📍</span>
        <span className="flex-1">{problemText(problem)}</span>
        {problem !== "unsupported" && (
          <button type="button" onClick={() => setOpen((o) => !o)} className="shrink-0 text-xs font-bold underline">
            {open ? "Hide" : "How to fix"}
          </button>
        )}
      </div>
      {open && problem !== "unsupported" && <Steps onRetry={onRetry} />}
    </div>
  );
}

/**
 * Home card for a canvasser: is location on for this site? Green line when it
 * is; otherwise a button that makes the phone ask, and the steps if it says no.
 */
export function LocationCheckCard() {
  const permission = useGeoPermission();
  const [tested, setTested] = useState<GeoResult | null>(null);
  const [busy, setBusy] = useState(false);

  const denied = permission === "denied" || (tested && !tested.ok && tested.problem === "denied");
  const working = !denied && (permission === "granted" || tested?.ok);

  async function test(): Promise<GeoResult> {
    setBusy(true);
    const r = await locate({ maxAgeMs: 0 });
    setBusy(false);
    setTested(r);
    return r;
  }

  if (working) {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
        <span>📍</span>
        <span>Location is on. The map shows where you are, and your shift is tracked while you're on it.</span>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900" role="alert">
        <div className="font-semibold">📍 Location is off for the CRM</div>
        <p className="mt-0.5 text-xs">The canvas can't show you on the map or track your shift until it's on. On this phone:</p>
        <Steps onRetry={test} />
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      <div className="font-semibold">📍 Turn on location before you knock</div>
      <p className="mt-0.5 text-xs">
        Your shift is tracked by GPS. Tap below, and when your phone asks, tap <strong>Allow</strong>.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void test()}
          disabled={busy}
          className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60"
        >
          {busy ? "Checking…" : "Turn on location"}
        </button>
        {tested && !tested.ok && <span className="text-xs">{problemText(tested.problem)}</span>}
      </div>
    </div>
  );
}
